import { useCallback, useEffect, useState } from "react";
import type { UseFloorPlanVisibility } from "./types";

const toggleHiddenIds = (
  previousHidden: Set<string>,
  candidateIds: string[],
): Set<string> => {
  if (candidateIds.length === 0) return previousHidden;

  const everyAlreadyHidden = candidateIds.every((id) => previousHidden.has(id));
  const nextHidden = new Set(previousHidden);

  if (everyAlreadyHidden) {
    candidateIds.forEach((id) => nextHidden.delete(id));
    return nextHidden;
  }

  candidateIds.forEach((id) => nextHidden.add(id));
  return nextHidden;
};

export const useFloorPlanVisibility: UseFloorPlanVisibility = ({
  resetKey,
}) => {
  const [hiddenElementIds, setHiddenElementIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [hiddenRouteIds, setHiddenRouteIds] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleElementsVisibility = useCallback((elementIds: string[]) => {
    setHiddenElementIds((previousHidden) =>
      toggleHiddenIds(previousHidden, elementIds),
    );
  }, []);

  const toggleRoutesVisibility = useCallback((routeIds: string[]) => {
    setHiddenRouteIds((previousHidden) =>
      toggleHiddenIds(previousHidden, routeIds),
    );
  }, []);

  const areAllElementsHidden = useCallback(
    (elementIds: string[]) => {
      if (elementIds.length === 0) return false;
      return elementIds.every((id) => hiddenElementIds.has(id));
    },
    [hiddenElementIds],
  );

  const areAllRoutesHidden = useCallback(
    (routeIds: string[]) => {
      if (routeIds.length === 0) return false;
      return routeIds.every((id) => hiddenRouteIds.has(id));
    },
    [hiddenRouteIds],
  );

  const resetVisibility = useCallback(() => {
    setHiddenElementIds(new Set());
    setHiddenRouteIds(new Set());
  }, []);

  useEffect(() => {
    setHiddenElementIds(new Set());
    setHiddenRouteIds(new Set());
  }, [resetKey]);

  return {
    hiddenElementIds,
    hiddenRouteIds,
    toggleElementsVisibility,
    toggleRoutesVisibility,
    areAllElementsHidden,
    areAllRoutesHidden,
    resetVisibility,
  };
};
